import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useContext,
} from "react";
import { Modal, Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { signOut } from "firebase/auth";
import Button from "../components/Button";
import UserContext from "../assets/user_context";
import { useAuth } from "./use-auth";
import { useThemeMode } from "../theme/ThemeContext";
import LoadingLogo from "../assets/logos/animated-logo.svg?react";
import LoadingLogoLight from "../assets/logos/animated-logo-light.svg?react";

// Constants
const localTesting = false;
const INACTIVITY_TIMEOUT = localTesting ? 20 * 1000 : 30 * 60 * 1000; // 20 seconds in dev, 30 minutes in prod
const WARNING_TIMEOUT = localTesting ? 15 * 1000 : 25 * 60 * 1000; // 15 seconds in dev, 25 minutes in prod
const STORAGE_KEY = "userActivity";
const SYNC_INTERVAL = 1000; // Check every second

// Styled components
const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "400px",
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[24],
  padding: theme.spacing(4),
  textAlign: "center",
}));

const TimeDisplay = styled(Typography)(({ theme }) => ({
  fontSize: "2rem",
  fontFamily: theme.typography.h4.fontFamily,
  color: theme.palette.error.main,
  margin: theme.spacing(2, 0),
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  justifyContent: "center",
  marginTop: theme.spacing(3),
}));

const LogoutSpinnerContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const SpinnerLogo = styled(Box)(({ theme }) => ({
  width: "80px",
  height: "80px",
  "& svg": {
    width: "100%",
    height: "100%",
  },
}));

// Activity data structure
const createActivityData = () => ({
  lastActivity: Date.now(),
  activeTab: true,
  tabId: Math.random().toString(36).substr(2, 9),
});

export const ActivityTracker = ({ children }) => {
  const authHook = useAuth();
  const { user, org } = useContext(UserContext);
  const { mode } = useThemeMode();
  const [showWarning, setShowWarning] = useState(false);
  const [showSignedOutMessage, setShowSignedOutMessage] = useState(false);
  const [showLogoutSpinner, setShowLogoutSpinner] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const activityDataRef = useRef(createActivityData());
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const syncIntervalRef = useRef(null);
  const initialSessionCheckComplete = useRef(false);

  // Check for expired session on mount and visibility change
  const checkForExpiredSession = useCallback(() => {
    if (localTesting)
      console.log("[SessionCheck] 🚀 checkForExpiredSession called");

    if (!user) {
      if (localTesting)
        console.log("[SessionCheck] ❌ No user, skipping session check");
      // Clear stale activity data when no user is logged in.
      // Prevents auto-logout from firing during sign-up due to old timestamps.
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    if (localTesting)
      console.log("[SessionCheck] 👤 User found, checking session...");

    // First, let's see what's in localStorage
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (localTesting)
      console.log("[SessionCheck] 📦 Raw localStorage data:", storedData);

    const mostRecentActivity = getMostRecentActivity();
    const timeSinceActivity = Date.now() - mostRecentActivity;
    const now = Date.now();

    if (localTesting) {
      console.log("[SessionCheck] 🔍 Detailed session check:", {
        currentTime: new Date(now).toLocaleString(),
        mostRecentActivity: new Date(mostRecentActivity).toLocaleString(),
        mostRecentActivityRaw: mostRecentActivity,
        timeSinceActivity: Math.floor(timeSinceActivity / 1000) + " seconds",
        timeSinceActivityMs: timeSinceActivity,
        timeoutThreshold: Math.floor(INACTIVITY_TIMEOUT / 1000) + " seconds",
        timeoutThresholdMs: INACTIVITY_TIMEOUT,
        shouldLogout: timeSinceActivity >= INACTIVITY_TIMEOUT,
        localTesting: localTesting,
      });

      // Also log what getMostRecentActivity is doing internally
      console.log("[SessionCheck] 🔍 getMostRecentActivity breakdown:");
      if (!storedData) {
        console.log(
          "[SessionCheck]   📭 No stored data, returning current time",
        );
      } else {
        try {
          const allTabsData = JSON.parse(storedData);
          console.log("[SessionCheck]   📊 All tabs data:", allTabsData);

          let mostRecent = 0;
          Object.entries(allTabsData).forEach(([tabId, tabData]) => {
            console.log(
              `[SessionCheck]   📋 Tab ${tabId}: last activity ${new Date(tabData.lastActivity).toLocaleString()}`,
            );
            if (tabData.lastActivity > mostRecent) {
              mostRecent = tabData.lastActivity;
            }
          });
          console.log(
            "[SessionCheck]   🏆 Most recent found:",
            new Date(mostRecent).toLocaleString(),
          );
        } catch (e) {
          console.log("[SessionCheck]   ❌ Error parsing stored data:", e);
        }
      }
    }

    // Mark initial session check as complete
    initialSessionCheckComplete.current = true;
    if (localTesting)
      console.log("[SessionCheck] ✅ Initial session check completed");

    // If the last activity was more than the timeout period ago, immediately log out
    if (timeSinceActivity >= INACTIVITY_TIMEOUT) {
      if (localTesting) {
        console.log(
          "[SessionCheck] 🚪 Session expired - logging out immediately",
        );
        console.log("[SessionCheck]   💥 LOGOUT TRIGGERED!");
      }
      handleAutoLogout();
    } else {
      // Session is still valid, reset timers based on actual time remaining
      const remainingTime = INACTIVITY_TIMEOUT - timeSinceActivity;
      const warningTime = WARNING_TIMEOUT - timeSinceActivity;

      if (localTesting) {
        console.log(
          "[SessionCheck] ✅ Session still valid, resetting timers:",
          {
            remainingTime: Math.floor(remainingTime / 1000) + " seconds",
            warningTime: Math.floor(warningTime / 1000) + " seconds",
          },
        );
      }

      // Now that session check is complete, update activity to current time for valid sessions
      const now = Date.now();
      activityDataRef.current.lastActivity = now;

      // Update localStorage with current activity
      const storedData = localStorage.getItem(STORAGE_KEY);
      let allTabsData = {};
      try {
        allTabsData = storedData ? JSON.parse(storedData) : {};
      } catch (e) {
        allTabsData = {};
      }

      allTabsData[activityDataRef.current.tabId] = {
        lastActivity: now,
        activeTab: true,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allTabsData));
      if (localTesting) {
        console.log(
          "[SessionCheck] 🔄 Activity timestamp updated after session validation",
        );
      }

      // Clear existing timers
      if (warningTimerRef.current) {
        if (localTesting)
          console.log("[SessionCheck]   🧹 Clearing existing warning timer");
        clearTimeout(warningTimerRef.current);
      }
      if (logoutTimerRef.current) {
        if (localTesting)
          console.log("[SessionCheck]   🧹 Clearing existing logout timer");
        clearTimeout(logoutTimerRef.current);
      }

      // Set warning timer if we haven't passed warning threshold yet
      if (warningTime > 0) {
        if (localTesting) {
          console.log(
            "[SessionCheck]   ⏰ Setting warning timer for",
            Math.floor(warningTime / 1000),
            "seconds",
          );
        }
        warningTimerRef.current = setTimeout(() => {
          if (localTesting)
            console.log("[SessionCheck]   ⚠️ Warning timer fired!");
          setShowWarning(true);
        }, warningTime);
      } else {
        // We're already past warning time, show warning immediately
        if (localTesting) {
          console.log(
            "[SessionCheck]   ⚠️ Already past warning time, showing warning immediately",
          );
        }
        setShowWarning(true);
      }

      // Set logout timer for remaining time
      if (localTesting) {
        console.log(
          "[SessionCheck]   ⏰ Setting logout timer for",
          Math.floor(remainingTime / 1000),
          "seconds",
        );
      }
      logoutTimerRef.current = setTimeout(() => {
        if (localTesting)
          console.log("[SessionCheck]   💥 Logout timer fired!");
        handleAutoLogout();
      }, remainingTime);
    }
  }, [user]);

  // Update activity in localStorage
  const updateActivity = useCallback(() => {
    if (!user) return;

    // Don't update activity timestamp until initial session check is complete
    if (!initialSessionCheckComplete.current) {
      if (localTesting) {
        console.log(
          "[SessionCheck] ⏸️ Activity update blocked - waiting for initial session check",
        );
      }
      return;
    }

    const now = Date.now();
    activityDataRef.current.lastActivity = now;

    if (localTesting) {
      console.log(
        "[SessionCheck] 🔄 Activity updated for tab:",
        activityDataRef.current.tabId,
        "at",
        new Date(now).toLocaleString(),
      );
    }

    // Get all tabs' activity
    const storedData = localStorage.getItem(STORAGE_KEY);
    let allTabsData = {};

    try {
      allTabsData = storedData ? JSON.parse(storedData) : {};
    } catch (e) {
      allTabsData = {};
    }

    // Update this tab's data
    allTabsData[activityDataRef.current.tabId] = {
      lastActivity: now,
      activeTab: true,
    };

    // Clean up old tabs (inactive for more than 5 seconds)
    const cleanedTabs = [];
    Object.keys(allTabsData).forEach((tabId) => {
      if (
        tabId !== activityDataRef.current.tabId &&
        now - allTabsData[tabId].lastActivity > 5000
      ) {
        cleanedTabs.push(tabId);
        delete allTabsData[tabId];
      }
    });

    if (cleanedTabs.length > 0 && localTesting) {
      console.log("[SessionCheck] 🧹 Cleaned up old tabs:", cleanedTabs);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(allTabsData));
    if (localTesting)
      console.log("[SessionCheck] 💾 Saved activity data:", allTabsData);

    // Reset timers
    resetTimers();
  }, [user]);

  // Get the most recent activity across all tabs
  const getMostRecentActivity = useCallback(() => {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (!storedData) return Date.now();

    try {
      const allTabsData = JSON.parse(storedData);
      let mostRecent = 0;

      Object.values(allTabsData).forEach((tabData) => {
        if (tabData.lastActivity > mostRecent) {
          mostRecent = tabData.lastActivity;
        }
      });

      return mostRecent || Date.now();
    } catch (e) {
      return Date.now();
    }
  }, []);

  // Handle auto logout
  const handleAutoLogout = useCallback(async () => {
    console.log("Auto-logout triggered due to inactivity");

    // Show logout spinner immediately
    setShowLogoutSpinner(true);

    // Hide warning modal and signed out message
    setShowWarning(false);
    setShowSignedOutMessage(false);

    // Clean up activity data
    localStorage.removeItem(STORAGE_KEY);

    // Sign out
    try {
      await signOut(authHook.auth);
      // After successful logout, hide spinner and show signed out message
      setShowLogoutSpinner(false);
      setShowSignedOutMessage(true);
    } catch (error) {
      console.error("Error during auto-logout:", error);
      // Even if logout fails, hide spinner and show error state
      setShowLogoutSpinner(false);
      setShowSignedOutMessage(true);
    }
  }, [authHook.auth]);

  // Reset timers based on activity
  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }

    // Hide warning and signed out message if showing
    if (showWarning) {
      setShowWarning(false);
    }
    if (showSignedOutMessage) {
      setShowSignedOutMessage(false);
    }

    // Set new warning timer
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
    }, WARNING_TIMEOUT);

    // Set new logout timer
    logoutTimerRef.current = setTimeout(() => {
      handleAutoLogout();
    }, INACTIVITY_TIMEOUT);
  }, [showWarning, showSignedOutMessage, handleAutoLogout]);

  // Handle manual sign out from warning modal
  const handleSignOut = async () => {
    // Show logout spinner immediately
    setShowLogoutSpinner(true);
    setShowWarning(false);

    // Clean up activity data
    localStorage.removeItem(STORAGE_KEY);

    // Sign out
    try {
      await signOut(authHook.auth);
      // After successful logout, hide spinner and show signed out message
      setShowLogoutSpinner(false);
      setShowSignedOutMessage(true);
    } catch (error) {
      console.error("Error during manual logout:", error);
      // Even if logout fails, hide spinner and show error state
      setShowLogoutSpinner(false);
      setShowSignedOutMessage(true);
    }
  };

  // Handle stay signed in
  const handleStaySignedIn = () => {
    updateActivity();
    setShowWarning(false);
  };

  // Handle dismiss signed out message
  const handleDismissSignedOut = () => {
    setShowSignedOutMessage(false);
  };

  // Clear modals when user changes (login/logout)
  useEffect(() => {
    if (!user) {
      // User logged out - hide spinner and warning
      setShowLogoutSpinner(false);
      setShowWarning(false);
      // Reset session check flag for next user
      initialSessionCheckComplete.current = false;
      if (localTesting) {
        console.log(
          "[SessionCheck] 🔄 Reset session check flag - user logged out",
        );
      }
    } else {
      // User logged in - hide signed out message and warning
      setShowSignedOutMessage(false);
      setShowWarning(false);
      setShowLogoutSpinner(false);
    }
  }, [user?.uid]);

  // Initial session check when user logs in or component mounts
  useEffect(() => {
    if (localTesting) {
      console.log(
        "[SessionCheck] 🏁 Initial session check useEffect triggered, user:",
        !!user,
      );
    }
    if (user) {
      if (localTesting) {
        console.log(
          "[SessionCheck] 👤 User detected, checking session immediately",
        );
      }
      // Check immediately without delay to prevent activity listeners from updating timestamp first
      checkForExpiredSession();
    } else {
      if (localTesting)
        console.log("[SessionCheck] ❌ No user for initial session check");
    }
  }, [user, checkForExpiredSession]);

  // Activity event listeners
  useEffect(() => {
    if (!user) return;

    // Activity events to track
    const activityEvents = [
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
      "mousemove", // Throttled below
    ];

    // Throttle mousemove events
    let lastMouseMove = 0;
    const handleActivity = (event) => {
      if (event.type === "mousemove") {
        const now = Date.now();
        if (now - lastMouseMove < 1000) return; // Only track once per second
        lastMouseMove = now;
      }
      updateActivity();
    };

    // Handle visibility change - check for expired session when tab becomes visible
    const handleVisibilityChange = () => {
      if (localTesting) {
        console.log(
          "[SessionCheck] 👁️ Visibility change detected, document.hidden:",
          document.hidden,
        );
      }
      if (!document.hidden) {
        // Tab became visible - check if session expired while away
        if (localTesting) {
          console.log(
            "[SessionCheck] ✅ Tab became visible, checking for expired session",
          );
        }
        checkForExpiredSession();
      } else {
        if (localTesting) console.log("[SessionCheck] 🙈 Tab became hidden");
      }
    };

    // Handle window focus - check for expired session when window gains focus
    const handleFocus = () => {
      if (localTesting) {
        console.log(
          "[SessionCheck] 🔍 Window gained focus, checking for expired session",
        );
      }
      checkForExpiredSession();
    };

    // Add event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Clean up on unmount
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [user, updateActivity, checkForExpiredSession]);

  // Sync with other tabs
  useEffect(() => {
    if (!user) return;

    // Check activity from other tabs periodically
    syncIntervalRef.current = setInterval(() => {
      const mostRecentActivity = getMostRecentActivity();
      const timeSinceActivity = Date.now() - mostRecentActivity;

      // If another tab is active, reset our timers
      if (
        timeSinceActivity < 5000 &&
        mostRecentActivity > activityDataRef.current.lastActivity
      ) {
        activityDataRef.current.lastActivity = mostRecentActivity;
        resetTimers();
      }

      // Update warning countdown if showing
      if (showWarning) {
        const remaining = INACTIVITY_TIMEOUT - timeSinceActivity;
        setTimeRemaining(Math.max(0, remaining));

        if (remaining <= 0) {
          handleAutoLogout();
        }
      }
    }, SYNC_INTERVAL);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [user, showWarning, getMostRecentActivity, resetTimers, handleAutoLogout]);

  // Listen for storage events (activity from other tabs)
  useEffect(() => {
    if (!user) return;

    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        // Another tab updated activity
        const mostRecentActivity = getMostRecentActivity();
        if (mostRecentActivity > activityDataRef.current.lastActivity) {
          activityDataRef.current.lastActivity = mostRecentActivity;
          resetTimers();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [user, getMostRecentActivity, resetTimers]);

  // Initial activity tracking
  useEffect(() => {
    if (!user) return;

    updateActivity();

    // Clean up on unmount or user change
    return () => {
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }

      // Remove this tab from activity tracking
      const storedData = localStorage.getItem(STORAGE_KEY);
      if (storedData) {
        try {
          const allTabsData = JSON.parse(storedData);
          delete allTabsData[activityDataRef.current.tabId];

          if (Object.keys(allTabsData).length > 0) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(allTabsData));
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        } catch (e) {
          // Ignore errors
        }
      }
    };
  }, [user, updateActivity]);

  // Format time remaining
  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <>
      {children}

      {/* Logout spinner modal */}
      <Modal
        open={showLogoutSpinner}
        onClose={() => {}} // Prevent closing
        disableEscapeKeyDown>
        <ModalBox>
          <LogoutSpinnerContainer>
            <SpinnerLogo>
              {mode === "dark" ? <LoadingLogoLight /> : <LoadingLogo />}
            </SpinnerLogo>
            <Typography variant="h6">Logging You Out...</Typography>
          </LogoutSpinnerContainer>
        </ModalBox>
      </Modal>

      {/* Session timeout warning modal */}
      <Modal
        open={showWarning}
        onClose={() => {}} // Prevent closing by clicking outside
        disableEscapeKeyDown // Prevent closing with ESC
      >
        <ModalBox>
          <Typography variant="h5" gutterBottom>
            Session Timeout Warning
          </Typography>
          <Typography variant="body1" color="text.secondary">
            For your safety, you will be signed out due to inactivity in:
          </Typography>
          <TimeDisplay>{formatTime(timeRemaining)}</TimeDisplay>
          <ButtonContainer>
            <Button variant="outlined" onClick={handleSignOut} color="error">
              Sign Out Now
            </Button>
            <Button variant="contained" onClick={handleStaySignedIn}>
              Stay Signed In
            </Button>
          </ButtonContainer>
        </ModalBox>
      </Modal>

      {/* Signed out notification modal */}
      <Modal open={showSignedOutMessage} onClose={handleDismissSignedOut}>
        <ModalBox>
          <Typography variant="h5" gutterBottom>
            Session Ended
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ marginBottom: 3 }}>
            You have been signed out to protect your data due to inactivity.
          </Typography>
          <Button
            variant="contained"
            onClick={handleDismissSignedOut}
            sx={{ minWidth: "120px" }}>
            Dismiss
          </Button>
        </ModalBox>
      </Modal>
    </>
  );
};

export default ActivityTracker;
